/* eslint no-alert: 0 */
import Auth from '@aws-amplify/auth';
import { push, replace } from 'react-router-redux';
import { parseParams, getLocation } from '../utils/url';
import { getStateAdfsProvider } from '../selectors';

import {
  APP_GIVE_CREDIT_CHECK_ACCESS,

  LOGIN_USER_REQUEST,
  LOGIN_USER_SUCCESS,
  LOGIN_USER_FAILURE,

  LOGIN_GIVE_HEADQUARTERS_ACCESS,
  LOGIN_GIVE_BRANCH_LEADER_SUPERVISOR_II_ACCESS,
  LOGIN_GIVE_BRANCH_LEADER_SUPERVISOR_ACCESS,
  LOGIN_GIVE_BRANCH_LEADER_ACCESS,
  LOGIN_GIVE_DEVELOPER_ACCESS,
  LOGIN_GIVE_DEVELOPER_SUPERVISOR,
  LOGIN_GIVE_DEVELOPER_SALES_MANAGER,
  LOGIN_GIVE_DEVELOPER_SALES_ADMIN,
  LOGIN_GIVE_DEVELOPER_STAFF,
  LOGIN_GIVE_EXTERNAL_AGENT_LEADER,
  LOGIN_GIVE_EXTERNAL_AGENT,
  LOGIN_GIVE_EXTERNAL_AGENCY_ADMIN,
  LOGIN_GIVE_DEVELOPER_SITEPLAN_ACCESS,
  LOGIN_GIVE_SUPER_ADMIN_ACCESS,
  LOGIN_GIVE_ADMIN_BASIC_ACCESS,
  LOGIN_GIVE_ADMIN_ACCESS,
  LOGIN_GIVE_MAIN_HEADQUARTERS_ACCESS,

  LOGIN_FROM_ADFS,

  LOGOUT_USER_REQUEST,
  LOGOUT_USER_SUCCESS,
  LOGIN_GIVE_EDIT_UNIT_PRICE_ACCESS,
  LOGIN_GIVE_EDIT_UNIT_DETAIL_ACCESS,
  LOGIN_GIVE_ADMIN_COMMUNITY_ACCESS,
} from '../constants/actions';
import {
  SHOWROOM,
  LOAN_TRACKER,
} from '../constants/apps';
import {
  DEVELOPER_HEADQUARTERS,
  DEVELOPER_BRANCH_LEADER_SUPERVISOR_II,
  DEVELOPER_BRANCH_LEADER_SUPERVISOR,
  DEVELOPER_BRANCH_LEADER,
  DEVELOPER_EXECUTIVE,
  DEVELOPER_AGENT_LEADER,
  DEVELOPER_AGENCY_ADMIN,
  DEVELOPER_SITEPLAN,
  SUPER_ADMIN,
  ADMIN_BASIC,
  ADMIN,
  ADMIN_COMMUNITY,
} from '../constants/groups';
import config from '../../config';
import { clearEndUser, identifyEndUser, registerTracePropagationOrigin } from '../lib/telemetry';
import { fetchM, serverRequestGet } from './fetchM';
import { handleError, userErrorIdentity } from './error';

// The decoded Cognito ID token of the current session — the token fetchM sends to the APIs, whose
// `sub` the Go tiers stamp as user.id. Only telemetry calls it, and only while tracing runs.
const readIdTokenClaims = () => Auth.currentSession()
  .then(session => session.getIdToken().decodePayload());

const signInOauth = (provider, nextBase64) => new Promise(async (resolve, reject) => {
  const authConfig = Auth.configure();
  const { oauth, userPoolWebClientId: clientId } = authConfig;
  if (!oauth) {
    return reject();
  }

  const {
    domain,
    redirectSignIn,
    scope,
    responseType,
  } = oauth;

  const query = {
    client_id: clientId,
    response_type: responseType,
    scope: scope.join(' '),
    redirect_uri: redirectSignIn,
  };

  if (provider) {
    query.identity_provider = provider.toUpperCase();
  }

  if (nextBase64) {
    query.state = nextBase64;
  }

  // The url of the Cognito Hosted UI
  const url = parseParams(`https://${domain}/oauth2/authorize`, query);

  // Launch hosted UI
  window.location.assign(url);
  return resolve();
});


function requestLogin() {
  return {
    type: LOGIN_USER_REQUEST,
  };
}

function loginError(message) {
  return {
    type: LOGIN_USER_FAILURE,
    payload: {
      statusText: message,
    },
  };
}

function giveHeadquartersAccess() {
  return {
    type: LOGIN_GIVE_HEADQUARTERS_ACCESS,
  };
}

function giveMainHeadquartersAccess() {
  return {
    type: LOGIN_GIVE_MAIN_HEADQUARTERS_ACCESS,
  };
}

function giveBranchLeaderSupervisorIIAccess() {
  return {
    type: LOGIN_GIVE_BRANCH_LEADER_SUPERVISOR_II_ACCESS,
  };
}

function giveBranchLeaderSupervisorAccess() {
  return {
    type: LOGIN_GIVE_BRANCH_LEADER_SUPERVISOR_ACCESS,
  };
}

function giveBranchLeaderAccess() {
  return {
    type: LOGIN_GIVE_BRANCH_LEADER_ACCESS,
  };
}

function giveSuperAdminAccess() {
  return {
    type: LOGIN_GIVE_SUPER_ADMIN_ACCESS,
  };
}

function giveAdminBasicAccess() {
  return {
    type: LOGIN_GIVE_ADMIN_BASIC_ACCESS,
  };
}

function giveAdminCommunityAccess() {
  return {
    type: LOGIN_GIVE_ADMIN_COMMUNITY_ACCESS,
  };
}

function giveAdminAccess() {
  return {
    type: LOGIN_GIVE_ADMIN_ACCESS,
  };
}

function giveDeveloperAccess() {
  return {
    type: LOGIN_GIVE_DEVELOPER_ACCESS,
  };
}

function giveDeveloperSupervisorAccess() {
  return {
    type: LOGIN_GIVE_DEVELOPER_SUPERVISOR,
  };
}

function giveDeveloperSalesManagerAccess() {
  return {
    type: LOGIN_GIVE_DEVELOPER_SALES_MANAGER,
  };
}

function giveDeveloperSalesAdminAccess() {
  return {
    type: LOGIN_GIVE_DEVELOPER_SALES_ADMIN,
  };
}

function giveDeveloperStaffAccess() {
  return {
    type: LOGIN_GIVE_DEVELOPER_STAFF,
  };
}

function giveExternalAgencyAdmin() {
  return {
    type: LOGIN_GIVE_EXTERNAL_AGENCY_ADMIN,
  };
}

function giveExternalAgentAccess() {
  return {
    type: LOGIN_GIVE_EXTERNAL_AGENT,
  };
}

function giveExternalAgentLeaderAccess() {
  return {
    type: LOGIN_GIVE_EXTERNAL_AGENT_LEADER,
  };
}

function giveDeveloperSiteplanAccess() {
  return {
    type: LOGIN_GIVE_DEVELOPER_SITEPLAN_ACCESS,
  };
}

function giveEditUnitPriceAccess() {
  return {
    type: LOGIN_GIVE_EDIT_UNIT_PRICE_ACCESS,
  };
}

function giveEditUnitDetailsAccess() {
  return {
    type: LOGIN_GIVE_EDIT_UNIT_DETAIL_ACCESS,
  };
}

function giveCreditCheckAccess() {
  return {
    type: APP_GIVE_CREDIT_CHECK_ACCESS,
  };
}

function receiveLogin(payload) {
  userErrorIdentity(payload);
  // OTEL-6: same hook Bugsnag uses to identify the user, for the same reason — a trace with no
  // caller cannot be tied back to whoever reported the problem. OTEL-95: user.id is the `sub` of
  // the session's ID token (never the username, which embeds an SSO user's email), read only
  // while tracing runs. No-op when tracing is disabled.
  identifyEndUser(payload, readIdTokenClaims);
  return {
    type: LOGIN_USER_SUCCESS,
    payload,
  };
}

function requestLogout() {
  return {
    type: LOGOUT_USER_REQUEST,
  };
}

function completeLogout() {
  // Drop the caller, so the next session's spans cannot inherit the last user.
  clearEndUser();
  return {
    type: LOGOUT_USER_SUCCESS,
  };
}

export function fetchCompanyCurrencies() {
  return async (dispatch, getState) => {
    const { auth } = getState();
    try {
      const response = await serverRequestGet(
        `/company/${auth.company.id}/currency/`,
        { dispatch },
      );
      if (response.error) {
        return null;
      }
      return dispatch({
        type: 'companyCurrency',
        payload: {
          data: response.data || [],
        },
      });
    } catch (e) {
      Bugsnag.notifyException(e);
      return null;
    }
  };
}

export function giveLoginFromADFS(provider) {
  return {
    type: LOGIN_FROM_ADFS,
    payload: {
      provider,
    },
  };
}

export function redirectToLogin(next = '/') {
  return () => {
    let nextBase64 = '';
    if (next) {
      try {
        nextBase64 = btoa(`${window.location.origin}${next}${window.location.search || ''}`);
      } catch (e) {
        Bugsnag.notifyException(e);
      }
    }
    const provider = getStateAdfsProvider();
    if (provider) {
      signInOauth(provider, nextBase64);
    } else {
      window.location.href = `${config.get('startURL')}/login?redirect=${nextBase64}`;
    }
  };
}

function isBranchLeader(groups) {
  return groups.some(group => (
    group.code === DEVELOPER_BRANCH_LEADER
  ));
}

function isBranchLeaderSupervisorII(groups) {
  return groups.some(group => (
    group.code === DEVELOPER_BRANCH_LEADER_SUPERVISOR_II
  ));
}

function isBranchLeaderSupervisor(groups) {
  return groups.some(group => (
    group.code === DEVELOPER_BRANCH_LEADER_SUPERVISOR
  ));
}

function isHeadquarters(groups) {
  return groups.some(group => (
    group.code === DEVELOPER_HEADQUARTERS
  ));
}

function isDeveloper(groups) {
  return groups.some(group => (
    group.code === DEVELOPER_EXECUTIVE
  ));
}

function isDeveloperAgentLeader(groups) {
  return groups.some(group => (
    group.code === DEVELOPER_AGENT_LEADER
  ));
}

function isDeveloperAgencyAdmin(groups) {
  return groups.some(group => (
    group.code === DEVELOPER_AGENCY_ADMIN
  ));
}

function isDeveloperSupervisor(groups) {
  return (
    isDeveloper(groups) &&
    isBranchLeader(groups) &&
    isBranchLeaderSupervisorII(groups) &&
    isBranchLeaderSupervisor(groups)
  );
}

function isDeveloperSalesManager(groups) {
  return (
    isDeveloper(groups) &&
    isBranchLeader(groups) &&
    isBranchLeaderSupervisorII(groups) &&
    !isBranchLeaderSupervisor(groups)
  );
}

function isDeveloperSalesAdmin(groups) {
  return (
    isDeveloper(groups) &&
    isBranchLeader(groups) &&
    !isBranchLeaderSupervisorII(groups) &&
    !isBranchLeaderSupervisor(groups)
  );
}

function isDeveloperStaff(groups, affiliate) {
  return (
    isDeveloper(groups) &&
    !isBranchLeader(groups) &&
    !isBranchLeaderSupervisorII(groups) &&
    !isBranchLeaderSupervisor(groups) &&
    !isHeadquarters(groups) &&
    (affiliate && affiliate.companyBranch && !affiliate.companyBranch.isExternal)
  );
}

function isExternalAgentLeader(groups, affiliate) {
  return (
    isDeveloper(groups) &&
    isDeveloperAgentLeader(groups) &&
    (affiliate && affiliate.companyBranch && affiliate.companyBranch.isExternal)
  );
}

function isExternalAgent(groups, affiliate) {
  return (
    isDeveloper(groups) &&
    !isDeveloperAgentLeader(groups) &&
    (affiliate && affiliate.companyBranch && affiliate.companyBranch.isExternal)
  );
}

function isExternalAgencyAdmin(groups, affiliate) {
  return (
    isDeveloper(groups) &&
    isDeveloperAgencyAdmin(groups) &&
    (affiliate && affiliate.companyBranch && affiliate.companyBranch.isExternal)
  );
}

function isDeveloperSiteplan(groups) {
  return groups.some(group => (
    group.code === DEVELOPER_SITEPLAN
  ));
}

function isMainHeadquarters(affiliate) {
  return affiliate.companyBranchID === affiliate.company.headquartersBranchID;
}

function isSuperAdmin(groups) {
  return groups.some(group => (
    group.code === SUPER_ADMIN
  ));
}

function isAdminBasic(groups) {
  return groups.some(group => (
    group.code === ADMIN_BASIC
  ));
}

function isAdminCommunity(groups) {
  return groups.some(group => (
    group.code === ADMIN_COMMUNITY
  ));
}

function isAdmin(groups) {
  return groups.some(group => (
    group.code === ADMIN
  ));
}

function checkGroupAccess(groups, affiliate) {
  return (dispatch) => {
    if (groups) {
      if (isHeadquarters(groups)) {
        dispatch(giveHeadquartersAccess());
        if (isMainHeadquarters(affiliate)) {
          dispatch(giveMainHeadquartersAccess());
        }
        if (affiliate && affiliate.access) {
          const { allowEditUnitPrice, allowEditUnitDetails } = affiliate.access;
          if (allowEditUnitPrice) {
            dispatch(giveEditUnitPriceAccess());
          }
          if (allowEditUnitDetails) {
            dispatch(giveEditUnitDetailsAccess());
          }
        }
      }
      if (isBranchLeader(groups)) {
        dispatch(giveBranchLeaderAccess());
        if (affiliate && affiliate.access) {
          const { allowEditUnitPrice, allowEditUnitDetails } = affiliate.access;
          if (allowEditUnitPrice) {
            dispatch(giveEditUnitPriceAccess());
          }
          if (allowEditUnitDetails) {
            dispatch(giveEditUnitDetailsAccess());
          }
        }
      }
      if (isBranchLeaderSupervisor(groups)) {
        dispatch(giveBranchLeaderSupervisorAccess());
      }
      if (isBranchLeaderSupervisorII(groups)) {
        dispatch(giveBranchLeaderSupervisorIIAccess());
      }
      if (isSuperAdmin(groups)) {
        dispatch(giveSuperAdminAccess());
        dispatch(giveEditUnitPriceAccess());
        dispatch(giveEditUnitDetailsAccess());
      }
      if (isDeveloper(groups)) {
        dispatch(giveDeveloperAccess());
      }
      if (isDeveloperSupervisor(groups)) {
        dispatch(giveDeveloperSupervisorAccess());
      }
      if (isDeveloperSalesManager(groups)) {
        dispatch(giveDeveloperSalesManagerAccess());
      }
      if (isDeveloperSalesAdmin(groups)) {
        dispatch(giveDeveloperSalesAdminAccess());
      }
      if (isDeveloperStaff(groups, affiliate)) {
        dispatch(giveDeveloperStaffAccess());
      }
      if (isExternalAgentLeader(groups, affiliate)) {
        dispatch(giveExternalAgentLeaderAccess());
      }
      if (isExternalAgent(groups, affiliate)) {
        dispatch(giveExternalAgentAccess());
      }
      if (isExternalAgencyAdmin(groups, affiliate)) {
        dispatch(giveExternalAgencyAdmin());
      }
      if (isDeveloperSiteplan(groups)) {
        dispatch(giveDeveloperSiteplanAccess());
      }
      if (isAdminBasic(groups)) {
        dispatch(giveAdminBasicAccess());
      }
      if (isAdminCommunity(groups)) {
        dispatch(giveAdminCommunityAccess());
      }
      if (isAdmin(groups)) {
        dispatch(giveAdminAccess());
      }
    }
  };
}

function checkCreditCheckAccess(companyApps = []) {
  return (dispatch) => {
    const ccApp = companyApps.find(a => a.appID === 'credit_check');
    if (ccApp && ccApp.hasAccess) {
      dispatch(giveCreditCheckAccess());
    }
  };
}

export function checkAuthenticated(nextBase64) {
  const opts = {
    credentials: 'include',
    headers: {
      'Cache-Control': 'no-cache',
      Pragma: 'no-cache',
    },
  };
  return (dispatch) => {
    dispatch(requestLogin());
    return dispatch(fetchM(`${config.get('apiURL')}/authenticated/`, opts))
      .then(res => res.json())
      .then((payload) => {
        let bookApiURL;
        let loanTrackerURL;
        const apps = payload.affiliate &&
          payload.affiliate.company &&
          payload.affiliate.company.apps ?
          payload.affiliate.company.apps : [];
        const groups = payload.groups || [];
        if (isSuperAdmin(groups)) {
          bookApiURL = prompt(
            'Enter 3 Character Code or SR API URL: ',
            config.get('env') === 'development' ?
              'http://api.mhub.local:9000' :
              'hub',
          );
        } else if (apps.length > 0) {
          const app = payload.affiliate.company.apps.find(a => a.appID === SHOWROOM);
          const loanApp = payload.affiliate.company.apps.find(a => a.appID === LOAN_TRACKER);
          if (app) {
            bookApiURL = app.apiURL;
            if (app.webURL) {
              // check if current URL matches app web URL config
              const currentURL = getLocation(window.location.href);
              const bookURL = getLocation(app.webURL);
              if (bookURL.host !== currentURL.host) {
                const startURL = config.get('startURL');
                window.location.href = startURL;
              }
            }
          }
          if (loanApp) {
            loanTrackerURL = loanApp.webURL;
          }
        }
        if (loanTrackerURL) {
          config.set('loanTrackerURL', loanTrackerURL);
        }
        if (bookApiURL) {
          let bApiURL = bookApiURL;
          if (bookApiURL.length === 3 && (config.get('server') === 'uat')) {
            bApiURL = `https://book-api-${bookApiURL.toLowerCase()}.uat.mhub.my`;
          } else if (bookApiURL.length === 3 && (config.get('env') !== 'development')) {
            bApiURL = `https://book-api-${bookApiURL.toLowerCase()}.mhub.my`;
          }
          config.set('bookApiURL', bApiURL);
          // OTEL-6: the Showroom API host is per-tenant and only known here, so the traceparent
          // allowlist cannot be built at init from config alone. Both web instrumentations re-read
          // propagateTraceHeaderCorsUrls per request, so registering now covers every later call.
          // No-op when tracing is disabled.
          registerTracePropagationOrigin(bApiURL);
        } else {
          const err = new Error('Showroom API not defined');
          const metadata = { payload: { data: JSON.stringify(payload) } };
          Bugsnag.notifyException(err, 'Auth error', metadata);
          dispatch(push('/forbidden/'));
          return;
        }
        dispatch(receiveLogin(payload));
        dispatch(checkGroupAccess(groups, payload.affiliate));
        dispatch(checkCreditCheckAccess(apps));
        let redirect = '/';
        if (nextBase64) {
          try {
            redirect = atob(nextBase64);
          } catch (e) {
            Bugsnag.notifyException(e);
          }
        }
        dispatch(replace(redirect));
      })
      .catch(err => dispatch(handleError(err, loginError)));
  };
}

export function logoutAndRedirect() {
  return async (dispatch) => {
    await dispatch(requestLogout());
    await dispatch(completeLogout());
    window.location.href = `${config.get('startURL')}/logout`;
    return Promise.resolve();
  };
}



// WEBPACK FOOTER //
// ./src/actions/auth.js